import { padEnd } from 'lodash-es';
import { FrontendLogLevel } from '~/api/frontend-log-level';

type LogConfig = {
	method: string;
	level: number;
};

const LOG_LEVEL_MAP: Record<FrontendLogLevel, LogConfig> = {
	[FrontendLogLevel.TRACE]: { method: 'trace', level: 1 },
	[FrontendLogLevel.DEBUG]: { method: 'debug', level: 2 },
	[FrontendLogLevel.LOG]: { method: 'log', level: 3 },
	[FrontendLogLevel.WARN]: { method: 'warn', level: 4 },
	[FrontendLogLevel.ERROR]: { method: 'error', level: 5 },
};
/**
 * 🦄 Acurity Logger 🦄
 * Only logs errors on PRODUCTION mode
 */
/* eslint-disable no-console */
export class Logger {
	/**
	 * Identifier for the logger
	 */
	private static readonly MARKER = '🦄';

	/**
	 * Length of log prefix, usually the caller's class name
	 */
	private static readonly logPrefixWidth: number = 20;

	private static logLevelThreshold: FrontendLogLevel = FrontendLogLevel.ERROR;

	public static setLogLevelThreshold(logLevel: FrontendLogLevel) {
		Logger.logLevelThreshold = logLevel;
		Logger.reinitializeMethods();
	}

	public static addPrefix(prefix: string, ...extras: any[]) {
		return (
			Logger.MARKER +
			' ' +
			padEnd(prefix, Logger.logPrefixWidth) +
			extras.join(' ')
		);
	}

	/**
	 * Returns a bound console method
	 */
	private static getLogAction: (
		logLevel: FrontendLogLevel,
		prefix?: string,
	) => (message?: any, ...optionalParams: any[]) => void = (
		logLevel: FrontendLogLevel,
		prefix: string = Logger.MARKER,
	) => {
		if (window.console && (DEVELOPMENT || Logger.shouldLog(logLevel))) {
			const consoleMethod = LOG_LEVEL_MAP[logLevel].method;
			return prefix.trim()
				? Function.prototype.bind.call(
						console[consoleMethod],
						console,
						prefix,
				  )
				: Function.prototype.bind.call(console[consoleMethod], console);
		}
		// NOTE: Jake: 2019-03-13 - WEBUI-1659
		// Do not change this return without great care.
		// Fixing this to return "void" rather than an empty JS Object took
		// hours to fix.
		// tslint:disable-next-line no-empty
		return (): void => {};
	};

	private static shouldLog(logLevel: FrontendLogLevel) {
		return (
			LOG_LEVEL_MAP[logLevel].level >=
			LOG_LEVEL_MAP[Logger.logLevelThreshold].level
		);
	}

	private static reinitializeMethods() {
		Logger.log = Logger.getLogAction(FrontendLogLevel.LOG);
		Logger.debug = Logger.getLogAction(FrontendLogLevel.DEBUG);
		Logger.warn = Logger.getLogAction(FrontendLogLevel.WARN);
		Logger.warning = Logger.getLogAction(FrontendLogLevel.WARN);
		Logger.error = Logger.getLogAction(FrontendLogLevel.ERROR);
		Logger.trace = Logger.getLogAction(FrontendLogLevel.TRACE);
	}

	/**
	 * `console.log`
	 */
	public static log = Logger.getLogAction(FrontendLogLevel.LOG);

	/**
	 * `console.debug`
	 */
	public static debug = Logger.getLogAction(FrontendLogLevel.DEBUG);

	/**
	 * `console.warn`
	 */
	public static warn = Logger.getLogAction(FrontendLogLevel.WARN);

	/**
	 * `console.warn`
	 */
	public static warning = Logger.getLogAction(FrontendLogLevel.WARN);

	/**
	 * `console.error`
	 */
	public static error = Logger.getLogAction(FrontendLogLevel.ERROR);

	/**
	 * `console.trace`
	 */
	public static trace = Logger.getLogAction(FrontendLogLevel.TRACE);
}
